import Head from 'next/head';

import type { IImage, IProperty } from '@/types/content';

const FALLBACK_IMAGE = 'https://direct.travelnest.com/favicon.png';

/** The smallest rendition of an image, which is plenty for a link preview. */
export const urlForImageTag = (
	image?: IImage,
	fallbackImage = FALLBACK_IMAGE,
) => {
	if (!image?.formats) return image?.original ?? fallbackImage;
	const smallest = Object.values(image.formats)
		.filter((format) => format.x_dimension)
		.sort((a, b) => a.x_dimension - b.x_dimension)[0];
	return smallest?.url ?? image.original ?? fallbackImage;
};

interface MetaTagsHeadProps {
	property: Pick<
		IProperty,
		'name' | 'long_description' | 'summary_description'
	>;
	images: IImage[];
	url?: string;
}

/** Title and Open Graph tags so the property looks right when shared. */
export const MetaTagsHead = ({ property, images, url }: MetaTagsHeadProps) => {
	const title = property.name || 'Travelnest Direct';
	const description = (
		property.summary_description ||
		property.long_description ||
		'Direct bookings'
	)
		.replace(/\s+/g, ' ')
		.slice(0, 150);
	const image = urlForImageTag(images[0]);

	return (
		<Head>
			<title>{title}</title>
			<meta name="description" content={description} />
			<meta property="og:title" content={title} />
			<meta property="og:type" content="website" />
			<meta property="og:description" content={description} />
			<meta property="og:image" content={image} />
			{url && <meta property="og:url" content={url} />}
			<meta name="twitter:card" content="summary_large_image" />
		</Head>
	);
};

export default MetaTagsHead;
